/**
 * Role-aware landing-page redirect — Wave 2.35 FE, TASK-107a.
 *
 * Mounted at the index `/` route inside the auth-guarded shell. Decides where
 * a freshly-authenticated user should land based on their role:
 *
 *  - selector             → `/selector/home`
 *  - everyone else        → `/dashboard`
 *
 * Why a component instead of a static `<Navigate>`: the previous wave routed
 * everyone to `/dashboard`, which works for admins but dumps the selector on
 * an admin-only page. Centralising the rule here keeps `LoginPage` /
 * `safeReturnTo` agnostic of role — they continue to default to `/`, and this
 * component dispatches to the correct cabinet.
 *
 * Returns `null` if the auth context has not produced a user yet (the outer
 * `<ProtectedRoute>` already renders a loader during silent-refresh).
 */

import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Center, Stack, Text, Title } from '@mantine/core';
import { useAuth, useLogout } from '@/hooks/use-auth';
import type { AuthUser } from '@/lib/auth-provider';

export const ADMIN_HOME_PATH = '/dashboard';
export const SELECTOR_HOME_PATH = '/selector/home';

const ADMIN_PANEL_ROLES = new Set(['admin', 'selector', 'super_admin']);

/**
 * BUG-090: client tenant roles (client_owner / client_manager) have no place
 * in the admin SPA. The BE rejects them at /auth/login, but a session that
 * predates the gate could still resolve via silent refresh — so we mirror the
 * predicate here and refuse to route such a user into `/dashboard`.
 */
export function canAccessAdminPanel(user: AuthUser | null): boolean {
  if (!user) return false;
  return ADMIN_PANEL_ROLES.has(user.role) || user.is_super_admin === true;
}

export function homePathForRole(role: string | undefined): string {
  return role === 'selector' ? SELECTOR_HOME_PATH : ADMIN_HOME_PATH;
}

function ClientRoleRejected() {
  const logout = useLogout();
  useEffect(() => {
    logout.mutate();
    // Run once on mount — the auth session must be torn down exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Center mih="100dvh">
      <Stack gap="xs" align="center" maw={420}>
        <Title order={2} fz={22}>
          Доступ запрещён
        </Title>
        <Text c="dimmed" ta="center">
          Этот раздел только для сотрудников musichead и селекторов. Клиентам —
          клиентское приложение.
        </Text>
      </Stack>
    </Center>
  );
}

export function RoleHomeRedirect() {
  const { user } = useAuth();
  if (!user) return null;
  if (!canAccessAdminPanel(user)) {
    return <ClientRoleRejected />;
  }
  return <Navigate to={homePathForRole(user.role)} replace />;
}
